import React, { useEffect, useRef, useState } from "react";
import { faArrowDown, faArrowUp, faChevronDown, faChevronRight, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { generateId } from "../../utils/Utils";
import { Habit, HabitStep } from "../../types";
import { useTranslation } from "react-i18next";
import { ALL_TRACKED_DAYS, formatDateToLocal, getHabitTrackedDays } from "../../utils/habitUtils";
import "./AddHabitModal.css";

interface AddHabitModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd?: (habit: Habit) => void;
    editElement?: Habit;
    onUpdate?: (habit: Habit) => void;
}

type HabitMode = 'normal' | 'smart';
interface HabitConfigForm {
    id: string;
    title: string;
    hasNote: boolean;
    note: string;
    hasTarget: boolean;
    count: string;
    unit: string;
    trackedDays: number[];
    frequency: Habit['frequency'];
    periodTargetDays: number | '';
}

const createConfigForm = (title = ''): HabitConfigForm => ({
    id: generateId(),
    title,
    hasNote: false,
    note: '',
    hasTarget: false,
    count: '',
    unit: '',
    trackedDays: ALL_TRACKED_DAYS,
    frequency: 'daily',
    periodTargetDays: 1,
});

const normalizeCount = (count: string) => {
    const parsedCount = count.trim() ? Number(count) : undefined;
    return parsedCount && Number.isFinite(parsedCount) && parsedCount > 0
        ? Math.floor(parsedCount)
        : undefined;
};

const normalizePeriodTargetDays = (value: number | '', frequency: Habit['frequency']) => {
    const maximum = frequency === 'weekly' ? 7 : 31;
    const target = typeof value === 'number' ? value : 1;
    return Math.min(maximum, Math.max(1, Math.floor(target)));
};

const stepToForm = (step: string | HabitStep | undefined, fallback: HabitConfigForm): HabitConfigForm => {
    if (!step) return fallback;
    if (typeof step === 'string') {
        return {
            ...fallback,
            title: step,
        };
    }

    return {
        id: step.id || generateId(),
        title: step.title || '',
        hasNote: !!step.note,
        note: step.note || '',
        hasTarget: !!step.count || !!step.unit,
        count: step.count ? String(step.count) : '',
        unit: step.unit || '',
        trackedDays: step.trackedDays && step.trackedDays.length > 0 ? step.trackedDays : ALL_TRACKED_DAYS,
        frequency: step.frequency || 'daily',
        periodTargetDays: step.periodTargetDays || 1,
    };
};

const formToStep = (form: HabitConfigForm): HabitStep => ({
    id: form.id,
    title: form.title.trim(),
    note: form.hasNote ? form.note.trim() || undefined : undefined,
    count: form.hasTarget ? normalizeCount(form.count) : undefined,
    unit: form.hasTarget ? form.unit.trim() || undefined : undefined,
    trackedDays: form.trackedDays,
    frequency: form.frequency,
    periodTargetDays: normalizePeriodTargetDays(form.periodTargetDays, form.frequency),
});

interface StepTitleInputProps {
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
}

const StepTitleInput: React.FC<StepTitleInputProps> = ({
    value,
    placeholder,
    onChange,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const resizeTextarea = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 44)}px`;
    };

    useEffect(() => {
        resizeTextarea();
    }, [value]);

    return (
        <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="add-habit-step-title-input"
            rows={1}
        />
    );
};

export const AddHabitModal: React.FC<AddHabitModalProps> = ({
    isOpen,
    onClose,
    onAdd,
    editElement,
    onUpdate,
}) => {
    const { t } = useTranslation();
    const isEditing = !!editElement;
    const [mode, setMode] = useState<HabitMode>('normal');
    const [normalHabit, setNormalHabit] = useState<HabitConfigForm>(createConfigForm());
    const [goal, setGoal] = useState<HabitConfigForm>(createConfigForm());
    const [steps, setSteps] = useState<HabitConfigForm[]>([createConfigForm()]);
    const [goalFeelsHard, setGoalFeelsHard] = useState(false);
    const [currentStepId, setCurrentStepId] = useState('');
    const [collapsedStepIds, setCollapsedStepIds] = useState<string[]>([]);
    const [expandedOptions, setExpandedOptions] = useState<string[]>([]);
    const [openPeriodTargetId, setOpenPeriodTargetId] = useState<string | undefined>(undefined);

    const dayLabels = [
        t('habitElement.monday'),
        t('habitElement.tuesday'),
        t('habitElement.wednesday'),
        t('habitElement.thursday'),
        t('habitElement.friday'),
        t('habitElement.saturday'),
        t('habitElement.sunday'),
    ];

    useEffect(() => {
        if (!isOpen) return;

        setOpenPeriodTargetId(undefined);

        if (editElement) {
            const normalForm: HabitConfigForm = {
                id: editElement.id,
                title: editElement.title || '',
                hasNote: !!editElement.note,
                note: editElement.note || '',
                hasTarget: !!editElement.count || !!editElement.unit,
                count: editElement.count ? String(editElement.count) : '',
                unit: editElement.unit || '',
                trackedDays: getHabitTrackedDays(editElement),
                frequency: editElement.frequency || 'daily',
                periodTargetDays: editElement.periodTargetDays || 1,
            };

            setNormalHabit(normalForm);

            if (editElement.smartHabit?.enabled) {
                const currentIndex = editElement.smartHabit.currentStepIndex || 0;
                const fallbackGoal = {
                    ...normalForm,
                    id: editElement.smartHabit.goal?.id || generateId(),
                    title: editElement.smartHabit.longTermGoal || editElement.title || '',
                };
                const nextGoal = stepToForm(editElement.smartHabit.goal, fallbackGoal);
                const nextSteps = (editElement.smartHabit.steps.length > 0
                    ? editElement.smartHabit.steps
                    : [normalForm.title]
                ).map((step, index) => stepToForm(step, {
                    ...normalForm,
                    id: index === currentIndex ? editElement.id : generateId(),
                    title: typeof step === 'string' ? step : normalForm.title,
                }));

                setMode('smart');
                setGoal(nextGoal);
                setSteps(nextSteps);
                setGoalFeelsHard(nextSteps.length > 1 || nextSteps[0]?.title !== nextGoal.title);
                setCurrentStepId(nextSteps[Math.min(currentIndex, nextSteps.length - 1)]?.id || nextSteps[0]?.id || '');
                setCollapsedStepIds(nextSteps
                    .filter((step, index) => index !== currentIndex)
                    .map((step) => step.id));
                setExpandedOptions([normalForm, nextGoal, ...nextSteps]
                    .filter((form) => form.hasNote || form.hasTarget)
                    .map((form) => form.id));
            } else {
                setMode('normal');
                setGoal(createConfigForm());
                setSteps([createConfigForm()]);
                setGoalFeelsHard(false);
                setCurrentStepId('');
                setCollapsedStepIds([]);
                setExpandedOptions(normalForm.hasNote || normalForm.hasTarget ? [normalForm.id] : []);
            }
        } else {
            setMode('normal');
            setNormalHabit(createConfigForm());
            setGoal(createConfigForm());
            const firstStep = createConfigForm();
            setSteps([firstStep]);
            setGoalFeelsHard(false);
            setCurrentStepId(firstStep.id);
            setCollapsedStepIds([]);
            setExpandedOptions([]);
        }
    }, [isOpen, editElement]);

    const updateConfig = (
        form: HabitConfigForm,
        update: Partial<HabitConfigForm>,
        setter: React.Dispatch<React.SetStateAction<HabitConfigForm>>,
    ) => {
        setter({ ...form, ...update });
    };

    const updateStep = (id: string, update: Partial<HabitConfigForm>) => {
        setSteps((currentSteps) => currentSteps.map((step) => (
            step.id === id ? { ...step, ...update } : step
        )));
    };

    const toggleTrackedDay = (form: HabitConfigForm, day: number, onChange: (trackedDays: number[]) => void) => {
        if (form.trackedDays.includes(day)) {
            if (form.trackedDays.length === 1) return;
            onChange(form.trackedDays.filter((currentDay) => currentDay !== day));
            return;
        }

        onChange([...form.trackedDays, day].sort((a, b) => a - b));
    };

    const addStep = () => {
        const nextStep = createConfigForm();
        setSteps((currentSteps) => [...currentSteps, nextStep]);
        setCollapsedStepIds((currentIds) => currentIds.filter((id) => id !== nextStep.id));
    };

    const deleteStep = (id: string) => {
        setSteps((currentSteps) => {
            if (currentSteps.length === 1) return currentSteps;

            const nextSteps = currentSteps.filter((step) => step.id !== id);
            if (currentStepId === id) {
                setCurrentStepId(nextSteps[0]?.id || '');
            }
            setCollapsedStepIds((currentIds) => currentIds.filter((collapsedId) => collapsedId !== id));
            return nextSteps;
        });
    };

    const toggleStepCollapsed = (id: string) => {
        setCollapsedStepIds((currentIds) => (
            currentIds.includes(id)
                ? currentIds.filter((collapsedId) => collapsedId !== id)
                : [...currentIds, id]
        ));
    };

    const moveStep = (id: string, direction: -1 | 1) => {
        setSteps((currentSteps) => {
            const index = currentSteps.findIndex((step) => step.id === id);
            const targetIndex = index + direction;
            if (index === -1 || targetIndex < 0 || targetIndex >= currentSteps.length) {
                return currentSteps;
            }

            const nextSteps = [...currentSteps];
            [nextSteps[index], nextSteps[targetIndex]] = [nextSteps[targetIndex], nextSteps[index]];
            return nextSteps;
        });
    };

    const toggleOptionsExpanded = (id: string) => {
        setExpandedOptions((currentIds) => (
            currentIds.includes(id)
                ? currentIds.filter((currentId) => currentId !== id)
                : [...currentIds, id]
        ));
    };

    const buildHabitFromConfig = (config: HabitConfigForm, smartHabit?: Habit['smartHabit']): Habit => ({
        ...(editElement || {}),
        id: editElement?.id || generateId(),
        type: 'habit',
        title: config.title.trim(),
        note: config.hasNote ? config.note.trim() || undefined : undefined,
        count: config.hasTarget ? normalizeCount(config.count) : undefined,
        unit: config.hasTarget ? config.unit.trim() || undefined : undefined,
        frequency: config.frequency,
        trackedDays: config.trackedDays,
        monthlyDay: undefined,
        periodTargetDays: config.frequency === 'daily'
            ? undefined
            : normalizePeriodTargetDays(config.periodTargetDays, config.frequency),
        completedDates: editElement?.completedDates || [],
        progressByDate: editElement?.progressByDate,
        smartHabit,
    });

    const handleSubmit = () => {
        if (mode === 'normal') {
            if (!normalHabit.title.trim()) return;
            const habit = buildHabitFromConfig(normalHabit, undefined);
            if (isEditing && onUpdate) onUpdate(habit);
            else onAdd?.(habit);
            onClose();
            return;
        }

        if (!goal.title.trim()) return;

        const validSteps = goalFeelsHard
            ? steps.filter((step) => step.title.trim())
            : [goal];
        if (validSteps.length === 0) return;

        const selectedStepId = currentStepId || validSteps[0]?.id || '';
        const currentIndex = Math.max(
            validSteps.findIndex((step) => step.id === selectedStepId),
            0,
        );
        const activeStep = validSteps[currentIndex] || validSteps[0];
        const existingSmartHabit = editElement?.smartHabit;
        const existingCurrentStep = existingSmartHabit?.steps[existingSmartHabit.currentStepIndex || 0];
        const existingCurrentStepId = typeof existingCurrentStep === 'object' ? existingCurrentStep.id : '';
        const hasChangedCurrentStep = existingCurrentStepId && existingCurrentStepId !== activeStep.id;

        const smartHabit: Habit['smartHabit'] = {
            enabled: true,
            goal: formToStep(goal),
            steps: validSteps.map(formToStep),
            currentStepIndex: currentIndex,
            currentStepStartedAt: hasChangedCurrentStep
                ? formatDateToLocal(new Date())
                : existingSmartHabit?.currentStepStartedAt || formatDateToLocal(new Date()),
            promptThreshold: existingSmartHabit?.promptThreshold || 3,
        };

        const habit = buildHabitFromConfig(activeStep, smartHabit);
        if (isEditing && onUpdate) onUpdate(habit);
        else onAdd?.(habit);
        onClose();
    };

    const renderDayPicker = (form: HabitConfigForm, onChange: (trackedDays: number[]) => void) => (
        <div className="add-habit-day-picker">
            {dayLabels.map((dayLabel, index) => {
                const isSelected = form.trackedDays.includes(index);
                const dayInitial = dayLabel.slice(0, 1);

                return (
                    <button
                        key={dayLabel}
                        type="button"
                        className={`add-habit-day-toggle ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleTrackedDay(form, index, onChange)}
                        aria-pressed={isSelected}
                        aria-label={dayLabel}
                    >
                        <span>{dayInitial}</span>
                    </button>
                );
            })}
        </div>
    );

    const renderScheduleFields = (form: HabitConfigForm, onChange: (update: Partial<HabitConfigForm>) => void) => (
        <div className="add-habit-field">
            <label>{t('addHabit.frequency')}</label>
            <div className="add-habit-frequency-selector">
                {(['daily', 'weekly', 'monthly'] as Habit['frequency'][]).map((frequency) => (
                    <button
                        key={frequency}
                        type="button"
                        className={form.frequency === frequency ? 'selected' : ''}
                        onClick={() => onChange({
                            frequency,
                            trackedDays: frequency === 'daily' ? ALL_TRACKED_DAYS : form.trackedDays,
                        })}
                    >
                        {t(`addHabit.${frequency}`)}
                    </button>
                ))}
            </div>
            {form.frequency === 'daily' && renderDayPicker(form, (trackedDays) => onChange({ trackedDays }))}
            {form.frequency !== 'daily' && (
                <div className="add-habit-period-target">
                    <label htmlFor={`period-target-${form.id}`}>
                        {t(form.frequency === 'weekly' ? 'addHabit.daysPerWeek' : 'addHabit.daysPerMonth')}
                    </label>
                    <div className="add-habit-period-select">
                        <button
                            id={`period-target-${form.id}`}
                            type="button"
                            className="add-habit-period-select-trigger"
                            aria-haspopup="listbox"
                            aria-expanded={openPeriodTargetId === form.id}
                            onClick={() => setOpenPeriodTargetId((currentId) => currentId === form.id ? undefined : form.id)}
                        >
                            {form.periodTargetDays || 1}
                            <FontAwesomeIcon icon={faChevronDown} className="add-habit-period-select-icon" aria-hidden="true" />
                        </button>
                        {openPeriodTargetId === form.id && (
                            <div className="add-habit-period-select-options" role="listbox" aria-labelledby={`period-target-${form.id}`}>
                                {Array.from({ length: form.frequency === 'weekly' ? 7 : 31 }, (_, index) => index + 1).map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        role="option"
                                        aria-selected={(form.periodTargetDays || 1) === value}
                                        className={(form.periodTargetDays || 1) === value ? 'selected' : ''}
                                        onClick={() => {
                                            onChange({ periodTargetDays: value });
                                            setOpenPeriodTargetId(undefined);
                                        }}
                                    >
                                        {value}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const renderConfigFields = (
        form: HabitConfigForm,
        onChange: (update: Partial<HabitConfigForm>) => void,
        titleLabel = t('addHabit.habitName'),
        showOptions = true,
        showTitleLabel = true,
        showTitleField = true,
    ) => (
        <>
            {showTitleField && (
                <div className="add-habit-field">
                    {showTitleLabel && <label>{titleLabel}</label>}
                    <input
                        type="text"
                        value={form.title}
                        onChange={(e) => onChange({ title: e.target.value })}
                        placeholder={t('addHabit.enterHabitName')}
                        className="add-habit-input"
                    />
                </div>
            )}
            {renderScheduleFields(form, onChange)}
            {showOptions && (
                <>
                    <button
                        type="button"
                        className="add-habit-more-options"
                        onClick={() => toggleOptionsExpanded(form.id)}
                    >
                        <FontAwesomeIcon icon={expandedOptions.includes(form.id) ? faChevronDown : faChevronRight} />
                        {t('addHabit.moreOptions')}
                    </button>
                    {expandedOptions.includes(form.id) && (
                        <div className="add-habit-options-panel">
                            <label className="add-habit-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={form.hasNote}
                                    onChange={(e) => onChange({ hasNote: e.target.checked })}
                                />
                                {t('addHabit.addNote')}
                            </label>
                            {form.hasNote && (
                                <div className="add-habit-field">
                                    <textarea
                                        value={form.note}
                                        onChange={(e) => onChange({ note: e.target.value })}
                                        placeholder={t('addHabit.enterHabitNote')}
                                        className="add-habit-textarea"
                                        rows={3}
                                    />
                                </div>
                            )}
                            <label className="add-habit-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={form.hasTarget}
                                    onChange={(e) => onChange({ hasTarget: e.target.checked })}
                                />
                                {t('addHabit.setCountAndUnit')}
                            </label>
                            {form.hasTarget && (
                                <div className="add-habit-target-row">
                                    <div className="add-habit-field">
                                        <label>{t('addHabit.count')}</label>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={form.count}
                                            onChange={(e) => onChange({ count: e.target.value })}
                                            placeholder={t('addHabit.enterCount')}
                                            className="add-habit-input"
                                        />
                                    </div>
                                    <div className="add-habit-field">
                                        <label>{t('addHabit.unit')}</label>
                                        <input
                                            type="text"
                                            value={form.unit}
                                            onChange={(e) => onChange({ unit: e.target.value })}
                                            placeholder={t('addHabit.enterUnit')}
                                            className="add-habit-input"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );

    const renderDifficultyQuestion = () => (
        <section className="add-habit-smart-section add-habit-difficulty-section">
            <label className="add-habit-checkbox-row add-habit-difficulty-check">
                <input
                    type="checkbox"
                    checked={goalFeelsHard}
                    onChange={(e) => {
                        setGoalFeelsHard(e.target.checked);
                        setCurrentStepId(e.target.checked ? steps[0]?.id || '' : goal.id);
                    }}
                />
                {t('addHabit.badDayQuestion')}
            </label>
        </section>
    );

    const renderStepCard = (step: HabitConfigForm, index: number, showActions: boolean) => (
        <div
            key={step.id}
            className={`add-habit-step-card ${step.id === currentStepId ? 'current' : ''} ${collapsedStepIds.includes(step.id) ? 'collapsed' : 'expanded'}`}
        >
            <div className="add-habit-step-header">
                <label className="add-habit-step-current-selector" title={t('addHabit.currentStep')}>
                    <input
                        type="radio"
                        name="current-smart-step"
                        checked={step.id === currentStepId}
                        onChange={() => setCurrentStepId(step.id)}
                    />
                    <span />
                </label>
                <button
                    type="button"
                    className="add-habit-step-collapse"
                    onClick={() => toggleStepCollapsed(step.id)}
                    aria-label={collapsedStepIds.includes(step.id) ? t('addHabit.expandStep') : t('addHabit.collapseStep')}
                >
                    <FontAwesomeIcon icon={collapsedStepIds.includes(step.id) ? faChevronRight : faChevronDown} />
                </button>
                <StepTitleInput
                    value={step.title}
                    onChange={(title) => updateStep(step.id, { title })}
                    placeholder={index === 0 ? t('addHabit.smallestStep') : t('addHabit.untitledStep')}
                />
                {showActions && (
                    <div className="add-habit-step-actions">
                        <button type="button" onClick={() => moveStep(step.id, -1)} disabled={index === 0}>
                            <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                        <button type="button" onClick={() => moveStep(step.id, 1)} disabled={index === steps.length - 1}>
                            <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                        <button type="button" onClick={() => deleteStep(step.id)} disabled={steps.length === 1}>
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                )}
            </div>
            {!collapsedStepIds.includes(step.id) && (
                renderConfigFields(
                    step,
                    (update) => updateStep(step.id, update),
                    index === 0 ? t('addHabit.smallestStepName') : t('addHabit.stepName'),
                    true,
                    false,
                    false,
                )
            )}
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="add-habit-modal-overlay" onClick={onClose}>
            <div className="add-habit-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{isEditing ? t('habitElement.editHabit') : t('addHabit.addHabit')}</h3>
                <div className="add-habit-mode-selector">
                    <label className={mode === 'normal' ? 'selected' : ''}>
                        <input
                            type="radio"
                            checked={mode === 'normal'}
                            onChange={() => setMode('normal')}
                        />
                        {t('addHabit.normalHabit')}
                    </label>
                    <label className={mode === 'smart' ? 'selected' : ''}>
                        <input
                            type="radio"
                            checked={mode === 'smart'}
                            onChange={() => setMode('smart')}
                        />
                        {t('addHabit.smartHabit')}
                    </label>
                </div>

                {mode === 'normal' ? (
                    renderConfigFields(normalHabit, (update) => updateConfig(normalHabit, update, setNormalHabit))
                ) : (
                    <div className="add-habit-smart-layout">
                        <div className="add-habit-goal-area">
                            <h4>{t('addHabit.goal')}</h4>
                            {renderConfigFields(goal, (update) => updateConfig(goal, update, setGoal), t('addHabit.goalName'), true, false)}
                        </div>
                        {goal.title.trim() && renderDifficultyQuestion()}
                        {goalFeelsHard && !isEditing && (
                            <section className="add-habit-smart-section">
                                <h4>{t('addHabit.easiestStepPrompt')}</h4>
                                {renderConfigFields(
                                    steps[0],
                                    (update) => updateStep(steps[0].id, update),
                                    t('addHabit.smallestStepName'),
                                    true,
                                    false,
                                )}
                                <p className="add-habit-helper-text">{t('addHabit.addMoreStepsLater')}</p>
                            </section>
                        )}
                        {goalFeelsHard && isEditing && (
                            <section className="add-habit-smart-section">
                                <div className="add-habit-smart-section-header">
                                    <h4>{t('addHabit.easiestStepPrompt')}</h4>
                                </div>
                                {steps.map((step, index) => renderStepCard(step, index, steps.length > 1))}
                                <button type="button" onClick={addStep} className="add-habit-step-add add-habit-step-add-bottom">
                                    <FontAwesomeIcon icon={faPlus} />
                                    {t('addHabit.addStep')}
                                </button>
                            </section>
                        )}
                    </div>
                )}

                <div className="add-habit-actions">
                    <button onClick={onClose} className="add-habit-cancel">{t('addHabit.cancel')}</button>
                    <button onClick={handleSubmit} className="add-habit-submit">
                        {isEditing ? t('habitElement.save') : t('addHabit.addHabitButton')}
                    </button>
                </div>
            </div>
        </div>
    );
};
